import { useState } from 'react';
import { Pencil } from 'lucide-react';

import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useForumPublish } from '@/hooks/useForum';
import { useToast } from '@/hooks/useToast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import type { ForumNode } from '@/lib/forum';

/**
 * Edit button + dialog for a forum node. Only renders for the original author.
 * Editing republishes the event with the same `d` tag (relays keep the latest
 * version), so it updates in place.
 */
export function EditNodeDialog({
  node,
  hasTitle = true,
  label = 'Edit',
}: {
  node: ForumNode;
  hasTitle?: boolean;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(node.title);
  const [content, setContent] = useState(node.content);
  const [busy, setBusy] = useState(false);
  const { user } = useCurrentUser();
  const { editNode } = useForumPublish();
  const { toast } = useToast();

  // Only the original author can edit their node (d-tag is per-pubkey).
  if (!user || user.pubkey !== node.pubkey) return null;

  function openDialog() {
    // Reset fields to the latest values each time the dialog opens.
    setTitle(node.title);
    setContent(node.content);
    setOpen(true);
  }

  async function submit() {
    if (hasTitle && !title.trim()) {
      toast({ title: 'A title is required' });
      return;
    }
    setBusy(true);
    try {
      await editNode(node, hasTitle ? title.trim() : '', content.trim());
      toast({ title: 'Updated' });
      setOpen(false);
    } catch (e) {
      toast({ title: 'Could not update', description: String(e) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          aria-label={label}
          onClick={openDialog}
          className="h-7 w-7 shrink-0 p-0 text-[#105B86] hover:bg-[#eaf2f9]"
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{label}</DialogTitle>
          <DialogDescription>
            Updates are published as a new version of this item. Only you (the
            author) can edit it.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {hasTitle && (
            <div className="space-y-1">
              <Label htmlFor="edit-title">Title</Label>
              <Input
                id="edit-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
          )}
          <div className="space-y-1">
            <Label htmlFor="edit-content">Message</Label>
            <Textarea
              id="edit-content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="min-h-32"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            className="bg-[#105B86] text-white hover:bg-[#0d4f74]"
            onClick={submit}
            disabled={busy}
          >
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
